import React from 'react';
import styled, { css } from 'styled-components';
import { ModalHeader, ModalLayout, ModalTitle, Tag, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { AchResponseType, IAchTransaction } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForUI } from 'utils/date';

const TransactionInfo = styled.div<{ marginBottom?: boolean }>`
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  padding: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.md)};

  p {
    word-break: break-word;
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(theme.space.lg)};
    `}
`;

const ModalSubTitle = styled(ModalTitle)`
  margin-top: ${toRem(theme.space.sm)};
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};
`;

const Row = styled.div`
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;

  p {
    max-width: ${toRem(180)};
  }
`;

const Column = styled.div`
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(theme.space['2xs'])};
`;

const Label = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

const StyledTag = styled(Tag)`
  margin-bottom: ${toRem(theme.space.sm)};
`;

const getStatusMessage = (type?: AchResponseType) => {
  switch (type) {
    case AchResponseType.RETURN:
      return <StyledTag dataCy="return-message" className="return-message" appearance={Appearance.DANGER} label="Returned" size={Size.SMALL} />;
    case AchResponseType.CHANGE_REQUEST:
      return <StyledTag dataCy="noc-message" appearance={Appearance.WARNING} label="Notification of Change" size={Size.SMALL} />;
    default:
      return <></>;
  }
};

const formatStatementDesc = (statementDesc: string) => {
  const desc = statementDesc.split('|');

  switch (desc.length) {
    case 3: {
      // entryDesc | companyName | paymentType -> companyName paymentType entryDesc
      const [entryDesc, companyName, paymentType] = desc;
      return `${companyName} ${paymentType} ${entryDesc}`;
    }
    case 2: {
      // entryDesc | companyName -> companyName entryDesc
      const [entryDesc, companyName] = desc;
      return `${companyName} ${entryDesc}`;
    }
    default:
      // entryDesc -> entryDesc (for legacy entries)
      return statementDesc;
  }
};

interface IAchTransactionDetails {
  transaction: IAchTransaction;
  onClose: () => void;
}

const AchTransactionDetails: React.FC<IAchTransactionDetails> = ({ transaction, onClose }) => (
  <ModalLayout maxWidth={540} isActive onClose={onClose}>
    <ModalHeader>
      {getStatusMessage(transaction?.achResponse?.type)}
      <ModalTitle>{transaction?.id}</ModalTitle>
      <ModalSubTitle>{centsToDollarsDisplay(transaction?.amount)}</ModalSubTitle>
    </ModalHeader>

    <TransactionInfo data-cy="transaction-info" marginBottom={!!transaction?.achResponse}>
      <Row>
        <Label size="small" fontWeight="medium">
          Date Entered
        </Label>
        <Text size="small">{transaction?.createdAt ? formatDateForUI(transaction.createdAt) : '--'}</Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Est. Settlement Date
        </Label>
        <Text size="small">{transaction?.estimatedSettlementAt ? formatDateForUI(transaction.estimatedSettlementAt) : '--'}</Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Type
        </Label>
        <Text size="small" capitalize>
          {transaction?.paymentType}
        </Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          ACH Status
        </Label>
        <Text size="small" capitalize>
          {transaction?.status}
        </Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Ledger
        </Label>
        <Text size="small" capitalize>
          {transaction?.ledger}
        </Text>
      </Row>
      <Row>
        <Label size="small" fontWeight="medium">
          Bank Account
        </Label>
        <Text size="small">{`**********${transaction?.bankAccountNumberLast4}`}</Text>
      </Row>
      {transaction?.statementDesc && (
        <Row>
          <Label size="small" fontWeight="medium">
            This will appear on your statement as:
          </Label>
          <Text size="small">{formatStatementDesc(transaction.statementDesc)}</Text>
        </Row>
      )}
    </TransactionInfo>

    {transaction?.achResponse && (
      <TransactionInfo data-cy="additional-info">
        <Column>
          <Label size="small" fontWeight="medium">
            Code
          </Label>
          <Text size="small">{transaction.achResponse.code || '--'}</Text>
        </Column>
        <Column>
          <Label size="small" fontWeight="medium">
            Description
          </Label>
          <Text size="small">{transaction.achResponse.desc || '--'}</Text>
        </Column>
        <Column>
          <Label size="small" fontWeight="medium">
            Additional Information
          </Label>
          <Text size="small">{transaction.achResponse.info || '--'}</Text>
        </Column>
      </TransactionInfo>
    )}
  </ModalLayout>
);

export default AchTransactionDetails;
